import {
  HStack,
  Spinner,
  Stack,
  useMediaQuery,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import actions from "src/actions";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import { VersionedTemplates } from "common/email-templates";
import { KYDOrganizationFull } from "@common/interfaces/KYDOrganization";
import ReviewCampaignForm from "./ReviewCampaignForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateCampaignPreview from "../CampaignPreview";
import {
  KYDInternalCampaign,
  KYDRenderedCampaignResponse,
} from "@common/interfaces/KYDCampaign";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";
import { formatAddress } from "common/services/util/address";

const ReviewCampaign = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [emailHtml, setEmailHtml] = useState<string>("");
  const [values, setValues] = useState<KYDInternalCampaign | null>(null);
  const [organization, setOrganization] = useState<KYDOrganizationFull | null>(
    null
  );
  const isMobile = useMediaQuery("(max-width: 768px)")[0];
  const [view, setView] = useState<"desktop" | "mobile">("desktop");
  const [organizationUsers, setOrganizationUsers] = useState<
    KYDOrganizationUser[]
  >([]);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    organizationUsers: true,
    campaign: true,
    organization: true,
    sendingCampaign: false,
    renderingEmail: true,
  });

  const fetchOrganizationUsers = useCallback(async () => {
    setLoading((prev) => ({ ...prev, organizationUsers: true }));
    try {
      const response = await actions.ADMIN.fetchOrganizationUsers();
      setOrganizationUsers(response.users);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading((prev) => ({ ...prev, organizationUsers: false }));
  }, []);

  const fetchCampaign = useCallback(async () => {
    setLoading((prev) => ({ ...prev, campaign: true }));
    try {
      const { campaign, html_string }: KYDRenderedCampaignResponse =
        await actions.ADMIN.fetchMarketingCampaign(id);
      setValues(campaign);
      setEmailHtml(html_string);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading((prev) => ({ ...prev, campaign: false }));
  }, [id, toast]);

  const fetchOrganization = useCallback(async () => {
    setLoading((prev) => ({ ...prev, organization: true }));
    try {
      const response = await actions.ADMIN.fetchOrganization();
      setOrganization(response);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading((prev) => ({ ...prev, organization: false }));
  }, [toast]);

  useEffect(() => {
    fetchOrganizationUsers();
    fetchCampaign();
    fetchOrganization();
  }, [fetchOrganizationUsers, fetchCampaign, fetchOrganization]);

  const launchCampaign = useCallback(async () => {
    setLoading((prev) => ({ ...prev, sendingCampaign: true }));
    try {
      await actions.ADMIN.launchMarketingCampaign(id);
      toast({
        title: "Campaign launched",
        description: "Campaign launched successfully",
        status: "success",
      });
      navigate(`/campaigns/${id}`);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
      });
    }
    setLoading((prev) => ({ ...prev, sendingCampaign: false }));
  }, [values, id]);

  if (
    loading.organizationUsers ||
    loading.campaign ||
    loading.organization ||
    !organization
  ) {
    return (
      <VStack
        h="100%"
        w="100%"
        justifyContent="center"
        alignItems="center"
        py={4}
      >
        <Spinner />
      </VStack>
    );
  }
  return (
    <VStack h="100%" w="100%" align="stretch" flex="1">
      <Stack
        direction={{ base: "column", md: "row" }}
        w="100%"
        h="100%"
        gap={0}
        justifyContent="flex-start"
        alignItems="stretch"
        flex="1"
      >
        <ShowIfHasScope
          permissions={permissions}
          checkFor={["crm/campaigns:write", "crm/write"]}
        >
          <ReviewCampaignForm
            onSubmit={launchCampaign}
            organizationUsers={organizationUsers}
            isMobile={isMobile}
            values={values}
            organization={organization}
            loading={loading.organizationUsers || loading.sendingCampaign}
          />
        </ShowIfHasScope>
        <CreateCampaignPreview
          showPreview={true}
          emailHtml={emailHtml || ""}
          view={view}
          onFinishRendering={() =>
            setLoading((prev) => ({ ...prev, renderingEmail: false }))
          }
          loading={loading.renderingEmail}
          setView={setView}
          isMobile={false}
        />
      </Stack>
    </VStack>
  );
};

export default ReviewCampaign;
